'use client';

import { useSelector } from '@xstate/react';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';

import { VerifyWithPasskeyContent } from '@flipster/features/src/passkey/VerifyWithPasskeyModal';
import { Modal } from '@flipster/react/design-system-legacy/modal';

import LoadingSpinner from '@web/features/modals/TimeTriggerTpslModal/components/LoadingSpinner';

import { passkeyListFlowBunja, passkeyMfaFlowBunja } from '../_passkey/bunja';
import RegisterMfaGuide from './RegisterMfaGuide';
import MfaForm from './_form/MfaForm';
import { mfaVerifyFlowBunja } from './bunja';

interface Props {
  onClose: () => void;
  onConfirm?: (mfaSessionToken: string) => unknown;
}

const MfaVerifyFlowModalContent = ({ onClose, onConfirm }: Props) => {
  const t = useTranslations();

  const { closePasskeyListModal } = useBunja(passkeyListFlowBunja);
  const { mfaWithPasskey, verifyStepAtom } = useBunja(passkeyMfaFlowBunja);
  const { mfaVerifyActorAtom } = useBunja(mfaVerifyFlowBunja);
  const mfaVerifyActor = useAtomValue(mfaVerifyActorAtom);
  const verifyStep = useAtomValue(verifyStepAtom);

  const { isLoading, isMfaRegisterPhase, isMfaPhase, isPasskeyPhase } = useSelector(mfaVerifyActor, (snapshot) => ({
    isLoading: snapshot.matches('INITIATING'),
    isMfaRegisterPhase: snapshot.matches('REQUIRE_MFA'),
    isMfaPhase: snapshot.matches('VERIFYING') || snapshot.matches('WAIT_FOR_DONE'),
    isPasskeyPhase: snapshot.matches('PASSKEY'),
  }));

  const handleClose = () => {
    onClose();
  };

  const handleConfirm = async (token: string) => {
    await onConfirm?.(token);
    mfaVerifyActor.send({ type: 'confirm' });
  };

  const getTitle = () => {
    if (isLoading) return ' ';
    if (isMfaRegisterPhase) return t('Mfa_verify.r250912.Authenticator_register');
    if (isMfaPhase) return t('Mfa_verify.r250710.Identity_verification_title');
    if (isPasskeyPhase) return t('Mfa_verify.r250917.Verify_with_passkey');
    return '';
  };

  const retryPasskey = () => {
    mfaVerifyActor.send({ type: 'passkey.retry' });
  };

  const handlePasskeyUnavailable = () => {
    mfaVerifyActor.send({ type: 'passkey.unavailable' });
  };

  useEffect(() => {
    const subscription = mfaVerifyActor.subscribe(async (snapshot) => {
      if (snapshot.matches('REQUIRE_MFA')) closePasskeyListModal();
      else if (snapshot.matches('PASSKEY')) {
        const { mfaScope, mfaSessionToken, authToken, needToVerify } = snapshot.context;

        await mfaWithPasskey({
          mfaScope,
          mfaSessionToken,
          authToken,
          onComplete: () => {
            const isPasskeyOnly = needToVerify.filter((channel) => channel !== 'passkey').length === 0;

            if (isPasskeyOnly) {
              onConfirm?.(mfaSessionToken);
            } else {
              mfaVerifyActor.send({ type: 'passkey.verified' });
            }
          },
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [mfaVerifyActor, closePasskeyListModal, mfaWithPasskey, onConfirm]);

  return (
    <>
      <Modal.Header>
        <Modal.Title label={getTitle()} />
      </Modal.Header>
      {isLoading && (
        <Modal.Content css={{ alignContent: 'center' }}>
          <LoadingSpinner />
        </Modal.Content>
      )}
      {isMfaRegisterPhase && <RegisterMfaGuide onClose={handleClose} />}
      {isMfaPhase && <MfaForm onClickConfirm={handleConfirm} />}
      {isPasskeyPhase && (
        <VerifyWithPasskeyContent
          verifyStep={verifyStep}
          onClickTryAgainButton={retryPasskey}
          onClickUnvailableButton={handlePasskeyUnavailable}
        />
      )}
    </>
  );
};

export default MfaVerifyFlowModalContent;
